// Screen 1 (spec 13 §Screens; design spec §9 `/login`): sign in, two ways, against Better Auth:
// email + password, or a one-time email link (magic link; invitees have no password). A session
// that ended (401 anywhere, api/session-expiry.ts) lands here with `expired`, and a used or
// expired link with `link=invalid`. The in-memory API lists its demo accounts in a notice.
// HARDEN-mfa: an account with two-step sign-in, on a device it does not trust, gets a second
// step after its password: a 6-digit authenticator code (or a recovery code), "Trust this device
// for 30 days", and a way back. An expired or exhausted challenge goes back to the password
// step. A sign-in link opened after enrolment lands here with `error=TWO_FACTOR_REQUIRED`.
import {
  Button,
  Checkbox,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Input,
  Notice,
  Segmented,
  Spinner,
} from "@companyos/ui";
import { useQueryClient } from "@tanstack/react-query";
import { createRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { type Api, challengeEnded } from "../api/client";
import { DEMO_ACCOUNTS } from "../api/fake";
import { AuthFrame, MagicLinkForm } from "../features/auth/auth-frame";
import { rootRoute } from "./__root";

type LoginSearch = {
  redirect?: string;
  expired?: boolean;
  link?: "invalid";
  /** Better Auth's magic-link error (`TWO_FACTOR_REQUIRED`: the account uses two-step). */
  error?: string;
};

/** Only same-app paths are followed after sign-in (no open redirect). */
export function safeRedirect(value: unknown): string {
  return typeof value === "string" && value.startsWith("/") && !value.startsWith("//")
    ? value
    : "/";
}

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "login",
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    ...(typeof search.redirect === "string" ? { redirect: search.redirect } : {}),
    ...(search.expired === true || search.expired === "true" ? { expired: true } : {}),
    ...(search.link === "invalid" ? { link: "invalid" as const } : {}),
    ...(typeof search.error === "string" ? { error: search.error } : {}),
  }),
  component: LoginPage,
});

function LoginPage() {
  const { api } = loginRoute.useRouteContext();
  const search = loginRoute.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  // HARDEN-mfa: a link refused because the account uses two-step sign-in goes to the password.
  const twoFactorLink = search.error === "TWO_FACTOR_REQUIRED";
  const [method, setMethod] = useState<"password" | "link">(
    search.link && !twoFactorLink ? "link" : "password",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [challenge, setChallenge] = useState(false);
  const target = safeRedirect(search.redirect);

  async function landed(path: string) {
    queryClient.removeQueries({ queryKey: ["me"] });
    await router.invalidate();
    router.history.push(path);
  }

  async function signedIn() {
    queryClient.removeQueries({ queryKey: ["me"] });
    await router.invalidate();
    await navigate({ to: target });
  }

  async function signIn(nextEmail: string, nextPassword: string) {
    setPending(true);
    setError(null);
    try {
      const step = await api.auth.signIn(nextEmail, nextPassword);
      if (step.twoFactor) {
        setPassword("");
        setChallenge(true);
      } else await signedIn();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sign-in failed.");
    } finally {
      setPending(false);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    void signIn(email, password);
  }

  if (challenge)
    return (
      <AuthFrame
        title="Two-step sign-in"
        description="Your account asks for a second step after the password."
      >
        <SecondStep
          api={api}
          onSignedIn={() => void signedIn()}
          onBack={(reason) => {
            setChallenge(false);
            setError(reason);
          }}
        />
      </AuthFrame>
    );

  return (
    <AuthFrame
      title="Sign in to CompanyOS"
      description="Use your work email."
      footer="Invited to a company? Open the link in your invitation email, or sign in here with an email link."
    >
      {search.expired ? (
        <Notice tone="warning" role="status">
          Your session ended. Sign in again to continue where you were.
        </Notice>
      ) : null}
      {twoFactorLink ? (
        <Notice tone="warning" role="status">
          This account uses two-step sign-in: sign in with your password and code.
        </Notice>
      ) : search.link ? (
        <Notice tone="warning" role="status">
          That sign-in link has expired or was already used. Send yourself a new one.
        </Notice>
      ) : null}
      <Segmented
        aria-label="Sign-in method"
        className="w-full [&>button]:flex-1"
        value={method}
        onValueChange={(v) => {
          setError(null);
          setMethod(v);
        }}
        options={[
          { value: "password", label: "Password" },
          { value: "link", label: "Email link" },
        ]}
      />
      {method === "password" ? (
        <form onSubmit={onSubmit} noValidate>
          <FieldGroup className="gap-4">
            <Field>
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                className="h-10"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="password">Password</FieldLabel>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                className="h-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            {error ? (
              <Notice tone="danger" role="alert">
                {error}
              </Notice>
            ) : null}
            <Button type="submit" size="lg" disabled={pending}>
              {pending ? <Spinner /> : null}
              Sign in
            </Button>
            <Link to="/reset-password" className="self-center text-sm text-secondary underline">
              Forgot password?
            </Link>
          </FieldGroup>
        </form>
      ) : (
        <MagicLinkForm api={api} callbackPath={target} onOpened={(path) => void landed(path)} />
      )}
      {api.mode === "fake" ? (
        <Notice tone="info" title="Demo accounts (in-memory API)" icon={null}>
          <ul className="mt-2 flex flex-col gap-1.5" aria-label="Demo accounts">
            {DEMO_ACCOUNTS.map((a) => (
              <li key={a.email}>
                <Button
                  variant="secondary"
                  className="h-auto w-full justify-start py-2 text-left"
                  disabled={pending}
                  onClick={() => void signIn(a.email, a.password)}
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{a.label}</span>
                    <span className="truncate text-xs text-muted-foreground">{a.email}</span>
                  </span>
                </Button>
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}
    </AuthFrame>
  );
}

/**
 * The sign-in challenge: an authenticator code, or a recovery code (each works once). Trusting
 * the device skips this step there for 30 days. A challenge that expired (10 minutes) or took
 * five wrong codes is over: back to the password, with the reason.
 */
function SecondStep({
  api,
  onSignedIn,
  onBack,
}: {
  api: Api;
  onSignedIn: () => void;
  onBack: (reason: string | null) => void;
}) {
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const typed = recovery ? code.trim() : code.replace(/\s/g, "");
  const ready = recovery ? typed.length > 0 : /^\d{6}$/.test(typed);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;
    setPending(true);
    setError(null);
    try {
      if (recovery) await api.auth.verifyRecoveryCode(typed, trust);
      else await api.auth.verifyTotp(typed, trust);
      onSignedIn();
    } catch (e) {
      const text = e instanceof Error ? e.message : "The code was not accepted.";
      if (challengeEnded(e)) onBack(text);
      else setError(text);
      setPending(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <FieldGroup className="gap-4">
        <Field data-invalid={error ? true : undefined}>
          <FieldLabel htmlFor="second-factor">
            {recovery ? "Recovery code" : "6-digit code"}
          </FieldLabel>
          <Input
            // A fresh field per kind: the browser's one-time-code autofill fits only the app code.
            key={recovery ? "recovery" : "totp"}
            id="second-factor"
            autoComplete={recovery ? "off" : "one-time-code"}
            inputMode={recovery ? "text" : "numeric"}
            className="h-10 font-mono tracking-widest tabular-nums"
            value={code}
            aria-invalid={error ? true : undefined}
            onChange={(e) => setCode(e.target.value)}
          />
          <FieldDescription>
            {recovery
              ? "One of the codes you saved when you set up two-step sign-in, like abcde-12345."
              : "From the authenticator app on your phone."}
          </FieldDescription>
        </Field>
        <Field orientation="horizontal">
          <Checkbox
            id="trust-device"
            checked={trust}
            onCheckedChange={(v) => setTrust(v === true)}
          />
          <FieldLabel htmlFor="trust-device" className="font-normal">
            Trust this device for 30 days
          </FieldLabel>
        </Field>
        {error ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : null}
        <Button type="submit" size="lg" disabled={pending || !ready}>
          {pending ? <Spinner /> : null}
          Verify
        </Button>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            type="button"
            variant="link"
            className="h-auto px-0"
            onClick={() => {
              setRecovery((r) => !r);
              setCode("");
              setError(null);
            }}
          >
            {recovery ? "Use your authenticator app instead" : "Use a recovery code instead"}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => onBack(null)}>
            Back to sign-in
          </Button>
        </div>
      </FieldGroup>
    </form>
  );
}
